import {createSlice} from '@reduxjs/toolkit';
import {cloneDeep, isEqual, isPlainObject} from 'lodash';
import packageInfo from '../package.json';
import {sortVehicleNamesHelper, fetchPostAuthSafe} from './app/utils';
import {resolveDisplayUnits} from '@ic/statsview-core/units.js';

/**
 * Defaults aligned with `modules/PhotoConfig.js` (server). Initial state and failed fetches use a deep copy.
 * Limit fields are always finite numbers or `Number.MAX_SAFE_INTEGER` for server "unlimited" (`null`).
 */
const defaultPhotoConfig = {
  storageMode: 'local',
  gcsBucket: 'intelliculture-shop-photos',
  localUploadDir: 'uploads/photos',
  limits: {
    maxPhotosPerServiceEntry: 20,
    maxPhotosPerInspectionReport: 10,
    maxPhotosPerBreakdownReport: 10,
    maxPhotosPerVehicle: Number.MAX_SAFE_INTEGER,
    maxPhotosPerImplement: Number.MAX_SAFE_INTEGER,
    maxFileSizeBytes: 15 * 1024 * 1024,
    maxImageWidthHeight: 1920,
    maxCompressedImageSizeMb: 0.8,
  },
  allowedContentTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/heic'],
  signedUrlUploadExpiryMs: 15 * 60 * 1000,
  signedUrlReadExpiryMs: 60 * 60 * 1000,
  mobile: {
    uploadQueueTTLMs: 3 * 24 * 60 * 60 * 1000,
    maxAttemptsPerImageFlush: 5,
    minPutRetryIntervalMs: 30 * 1000,
    defaultCompressQuality: 0.8,
    maxImageWidthHeight: 1920,
  },
};

const PHOTO_LIMIT_NUMERIC_KEYS = [
  'maxPhotosPerServiceEntry',
  'maxPhotosPerInspectionReport',
  'maxPhotosPerBreakdownReport',
  'maxPhotosPerVehicle',
  'maxPhotosPerImplement',
  'maxFileSizeBytes',
];

/**
 * Server may send `null` for "unlimited"; store always uses a number.
 * @param {object} cfg
 * @return {object}
 */
function normalizePhotoConfigNumbers(cfg) {
  const next = cloneDeep(cfg);
  const L = next.limits;
  if (!L) {
    return next;
  }
  for (const k of PHOTO_LIMIT_NUMERIC_KEYS) {
    if (L[k] === null || L[k] === undefined || L[k] === -1) {
      L[k] = Number.MAX_SAFE_INTEGER;
    }
  }
  return next;
}

/**
 * Deep-merge `patch` into a clone of `base`: nested plain objects recurse; arrays and scalars replace.
 * @param {object} base
 * @param {object} patch
 * @return {object}
 */
function mergePhotoConfig(base, patch) {
  if (!isPlainObject(patch)) {
    return normalizePhotoConfigNumbers(cloneDeep(base));
  }
  const out = cloneDeep(base);
  mergePhotoConfigDeep(out, patch);
  return normalizePhotoConfigNumbers(out);
}

/**
 * Mutates `target` in place (expected to be a fresh clone).
 * @param {object} target
 * @param {object} patch
 */
function mergePhotoConfigDeep(target, patch) {
  for (const key of Object.keys(patch)) {
    const pv = patch[key];
    if (pv === undefined) {
      continue;
    }
    if (Array.isArray(pv)) {
      target[key] = pv.slice();
    } else if (isPlainObject(pv) && isPlainObject(target[key])) {
      mergePhotoConfigDeep(target[key], pv);
    } else {
      target[key] = pv;
    }
  }
}

const defaultAuthStatus = {
  loggedIn: false,
  cabviewEnabled: false,
  cropviewEnabled: false,
  shopviewEnabled: false,
  inspectionEnabled: false,
  breakdownEnabled: false,
  laborEnabled: false,
  laborProductEnabled: false,
  zoneInfoEnabled: false,
  dashboardEnabled: false,
  alertviewEnabled: false,
  aiChatEnabled: false,
  sttEnabled: false,
  installEnabled: false,
  reportBuilderEnabled: false,
  reportBuilderMcpEnabled: false,
  extensionsEnabled: false,
  extensions: [],
};

const appSlice = createSlice({
  name: 'appSlice',
  initialState: {
    authenticationStatus: {...defaultAuthStatus},
    customerName: '',
    customerSettings: {},
    customerManagementAccess: {},
    customerViewAccess: {},
    userName: '',
    userSettings: {},
    auth: null,
    initialized: false,
    reloadApp: false,
    vehicles: [],
    vehicleLabels: [],
    appFilters: {},
    databaseUserNames: {},
    isUserLanguageReloading: false,
    isUserLanguageSwitchAvailable: false,
    googleMapsApiKey: null,
    photoConfig: cloneDeep(defaultPhotoConfig),
  },
  reducers: {
    updatePhotoConfig(state, action) {
      if (action.payload == null) {
        const reset = cloneDeep(defaultPhotoConfig);
        if (!isEqual(state.photoConfig, reset)) {
          state.photoConfig = reset;
        }
        return;
      }
      const merged = mergePhotoConfig(cloneDeep(defaultPhotoConfig), action.payload);
      if (!isEqual(state.photoConfig, merged)) {
        state.photoConfig = merged;
      }
    },
    updateCustomerInfo(state, action) {
      const customerInfo = action.payload;
      if (!customerInfo || typeof customerInfo.name === 'undefined' || typeof customerInfo.settings === 'undefined') {
        return;
      }

      if (!isEqual(state.customerName, customerInfo.name)) {
        state.customerName = customerInfo.name;
      }
      if (!isEqual(state.customerSettings, customerInfo.settings)) {
        state.customerSettings = customerInfo.settings;
      }
      if (!isEqual(state.customerManagementAccess, customerInfo.managementAccess)) {
        state.customerManagementAccess = customerInfo.managementAccess;
      }
      if (!isEqual(state.customerViewAccess, customerInfo.viewAccess)) {
        state.customerViewAccess = customerInfo.viewAccess;
      }
    },
    updateUserAccountInfo(state, action) {
      const userAccount = action.payload;
      if (!userAccount || typeof userAccount.settings === 'undefined') {
        return;
      }

      if (!isEqual(state.userSettings, userAccount.settings)) {
        state.userSettings = userAccount.settings;
      }

      // "default" means the account's units. The same helper answers the builder's account route.
      const resolvedUnits = resolveDisplayUnits(state.userSettings.general, state.customerSettings.general);
      state.userSettings.general.unitsLength = resolvedUnits.unitsLength;
      state.userSettings.general.unitsArea = resolvedUnits.unitsArea;
      state.isUserLanguageReloading = false;
    },
    updateDatabaseUserNamesDict(state, action) {
      const databaseUserNames = action.payload;

      if (!isEqual(state.databaseUserNames, databaseUserNames)) {
        state.databaseUserNames = databaseUserNames;
      }
    },
    setAuthStatus(state, action) {
      const authenticationStatus = action.payload;

      if (!isEqual(state.authenticationStatus, authenticationStatus)) {
        state.authenticationStatus = authenticationStatus;
      }
    },
    setAuth(state, action) {
      const auth = action.payload;
      state.auth = auth;
    },
    setInitialized(state, action) {
      const initialized = action.payload;
      state.initialized = initialized;
    },
    setIsUserLanguageReloading(state, action) {
      state.isUserLanguageReloading = action.payload;
    },
    setIsUserLanguageSwitchAvailable(state, action) {
      state.isUserLanguageSwitchAvailable = action.payload;
    },
    setReloadApp(state, action) {
      state.reloadApp = action.payload;
    },
    updateVehicles(state, action) {
      state.vehicles = action.payload;
    },
    updateVehicleLabels(state, action) {
      state.vehicleLabels = action.payload;
    },
    updateAppFilters(state, action) {
      state.appFilters = action.payload;
    },
    setGoogleMapsApiKey(state, action) {
      state.googleMapsApiKey = action.payload;
    },
  },
});

const APP_SESSION_RENEW_RELOAD_KEY = 'icAppSessionRenewReload';

/**
 * @param {string} key
 * @return {boolean}
 */
function readSessionFlag(key) {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch (error) {
    return false;
  }
}

/**
 * @param {string} key
 */
function writeSessionFlag(key) {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch (error) {
    // Storage unavailable; the renewal itself already happened
  }
}

/**
 * Move this CabView app session to a new session ID and token (one time per session).
 *
 * Uses fetch with an options object: the app only adds its platform header to fetch calls that pass one,
 * and it saves the new ID and token from the response because the URL contains /auth.
 *
 * @return {Promise<boolean>} whether the session moved
 */
async function renewAppSession() {
  try {
    const response = await fetch('/auth/renewAppSession', {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({}),
      cache: 'no-store',
    });
    const result = await response.json();
    return result.renewed === true;
  } catch (error) {
    console.error('[renewAppSession] failed:', error);
    return false;
  }
}

function checkAuthentication() {
  return async (dispatch, getState) => {
    try {
      const checkAuthResponse = await fetch(`/checkAuthentication?v=${packageInfo.version}`, {cache: 'no-store'});
      const checkAuthResult = await checkAuthResponse.json();

      // Renew at most once per page session: if the new cookie somehow did not stick, carry on rather
      // than reload in a loop
      if (checkAuthResult.appSessionRenewalDue && !readSessionFlag(APP_SESSION_RENEW_RELOAD_KEY)) {
        if (await renewAppSession()) {
          writeSessionFlag(APP_SESSION_RENEW_RELOAD_KEY);
          // Reload so the live data socket reconnects on the new session
          window.location.reload();
          return;
        }
      }

      dispatch(appSlice.actions.setReloadApp(checkAuthResult.reloadApp));
      dispatch(appSlice.actions.setAuthStatus(checkAuthResult));
    } catch (error) {
      console.error(error);
      dispatch(appSlice.actions.setAuthStatus(defaultAuthStatus));
    }
  };
}

function getPhotoConfig() {
  return async (dispatch) => {
    try {
      const photoConfigResponse = await fetch('/getPhotoConfig', {cache: 'no-store'});
      if (!photoConfigResponse.ok) {
        return;
      }
      const photoConfig = await photoConfigResponse.json();
      dispatch(appSlice.actions.updatePhotoConfig(photoConfig));
    } catch (error) {
      return;
    }
  };
}

function getCustomerInfo() {
  return async (dispatch, getState) => {
    try {
      const customerInfoResponse = await fetch('/getCustomerInfo', {cache: 'no-store'});
      const customerInfo = await customerInfoResponse.json();
      dispatch(appSlice.actions.updateCustomerInfo(customerInfo));
    } catch (error) {
      console.error('[appSlice] getCustomerInfo fetch failed, keeping existing customerSettings:', error);
    }
  };
}

function getUserAccountInfo() {
  return async (dispatch, getState) => {
    try {
      const userAccountResponse = await fetch('/getUserAccount', {cache: 'no-store'});
      const userAccount = await userAccountResponse.json();

      dispatch(appSlice.actions.updateUserAccountInfo(userAccount));
    } catch (error) {
      console.error('[appSlice] getUserAccountInfo fetch failed, keeping existing userSettings:', error);
    }
  };
}

function getCustomerAndUserInfo() {
  return async (dispatch, getState) => {
    try {
      const customerInfoResponse = await fetch('/getCustomerInfo', {cache: 'no-store'});
      const customerInfo = await customerInfoResponse.json();
      dispatch(appSlice.actions.updateCustomerInfo(customerInfo));
    } catch (error) {
      console.error(
        '[appSlice] getCustomerAndUserInfo: getCustomerInfo fetch failed, keeping existing customerSettings:',
        error
      );
    }

    try {
      const userAccountResponse = await fetch('/getUserAccount', {cache: 'no-store'});
      const userAccount = await userAccountResponse.json();

      dispatch(appSlice.actions.updateUserAccountInfo(userAccount));
    } catch (error) {
      console.error(
        '[appSlice] getCustomerAndUserInfo: getUserAccount fetch failed, keeping existing userSettings:',
        error
      );
    }
  };
}

function getDatabaseUserNames() {
  return async (dispatch, getState) => {
    // Skip fetch if data already loaded
    const state = getState();
    if (state.app.databaseUserNames && Object.keys(state.app.databaseUserNames).length > 0) {
      return;
    }

    try {
      const databaseUserNamesResponse = await fetch('/getDatabaseUserNames', {cache: 'no-store'});
      const databaseUserNames = await databaseUserNamesResponse.json();

      dispatch(appSlice.actions.updateDatabaseUserNamesDict(databaseUserNames));
    } catch (error) {
      dispatch(appSlice.actions.updateDatabaseUserNamesDict({}));
    }
  };
}

function getGoogleMapsApiKey() {
  return async (dispatch, getState) => {
    const state = getState();
    if (state.app.googleMapsApiKey) return;

    try {
      const response = await fetch('/getGoogleMapsApiKey', {cache: 'no-store'});
      const data = await response.json();
      dispatch(appSlice.actions.setGoogleMapsApiKey(data.apiKey || ''));
    } catch (error) {
      console.error('Failed to fetch Google Maps API key:', error);
    }
  };
}

function getGlobalVehicleData() {
  return async (dispatch, getState) => {
    // Get vehicle information
    const getVehiclesRequest = fetch('/getVehiclesNoAppFilters', {cache: 'no-store'});
    const getVehicleLabelsRequest = fetch('/getVehicleLabels', {cache: 'no-store'});
    const getFilterSessionDataRequest = fetch('/getFilterSessionData', {cache: 'no-store'});

    // Make all calls
    const [getVehiclesResponse, getVehicleLabelsResponse, getFilterSessionDataResponse] = await Promise.all([
      getVehiclesRequest,
      getVehicleLabelsRequest,
      getFilterSessionDataRequest,
    ]);

    // Get vehicles collection
    const vehicles = await getVehiclesResponse.json();
    const vehicleLabels = await getVehicleLabelsResponse.json();
    const filterSessionData = await getFilterSessionDataResponse.json();

    vehicles.sort((a, b) => {
      return sortVehicleNamesHelper(a.name, b.name);
    });

    dispatch(appSlice.actions.updateVehicles(vehicles));
    dispatch(appSlice.actions.updateVehicleLabels(vehicleLabels));
    dispatch(appSlice.actions.updateAppFilters(filterSessionData));
  };
}

function getVehicleLabels() {
  return async (dispatch) => {
    const response = await fetch('/getVehicleLabels', {cache: 'no-store'});
    const vehicleLabels = await response.json();
    dispatch(appSlice.actions.updateVehicleLabels(vehicleLabels));
  };
}

/**
 * Clear browser stored state that belongs to one customer. Cabview selections and AI chat threads
 * reference customer scoped ids, so they must not survive a switch.
 */
function clearCustomerScopedBrowserState() {
  try {
    localStorage.removeItem('chat_customer');
    localStorage.removeItem('cabviewUiState');
    sessionStorage.removeItem('chat_session_id');
    sessionStorage.removeItem('chat_thread_id');
    sessionStorage.removeItem('chat_constant_overrides');
  } catch (error) {
    // Storage can be unavailable in private browsing modes - a failure here is not fatal.
    console.error('[appSlice] failed clearing customer scoped browser state:', error);
  }
}

/**
 * Switch the session to another customer. Treated as a soft re-login.
 *
 * @param {string} customerName - database name of the customer to switch to
 * @param {string} sessionSyncUserName - current session user, for the post auth guard
 * @param {string} sessionSyncDb - current session customer, for the post auth guard
 * @return {Function} thunk resolving to {success, errorMsg}
 */
function switchCustomer(customerName, sessionSyncUserName, sessionSyncDb) {
  return async () => {
    const options = {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({database: customerName}),
    };

    let result;
    try {
      const response = await fetchPostAuthSafe('/settings/changeDatabase', options, sessionSyncUserName, sessionSyncDb);
      result = await response.json();
    } catch (error) {
      console.error('[appSlice] switchCustomer request failed:', error);
      return {success: false, errorMsg: 'Could not reach the server'};
    }

    if (!result || !result.success) {
      return {success: false, errorMsg: result?.errorMsg};
    }

    clearCustomerScopedBrowserState();

    return {success: true};
  };
}

/**
 * Start emulating another user's settings, or stop when passed an empty username. ic_admin only,
 * enforced server side. The caller reloads on success so every view refetches.
 *
 * @param {string} targetUserName - user to emulate, empty string to stop
 * @param {string} sessionSyncUserName - the real session user, for the post auth guard
 * @param {string} sessionSyncDb - current session customer, for the post auth guard
 * @return {Function} thunk resolving to {success, errorMsg}
 */
function emulateUser(targetUserName, sessionSyncUserName, sessionSyncDb) {
  return async () => {
    const options = {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({username: targetUserName}),
    };

    let result;
    try {
      const response = await fetchPostAuthSafe('/settings/emulateUser', options, sessionSyncUserName, sessionSyncDb);
      result = await response.json();
    } catch (error) {
      console.error('[appSlice] emulateUser request failed:', error);
      return {success: false, errorMsg: 'Could not reach the server'};
    }

    if (!result || !result.success) {
      return {success: false, errorMsg: result?.errorMsg};
    }

    // Emulated settings drive cached slices. Clearing browser storage is all that happens here -
    // the caller reloads the page on success, which is what actually drops the redux cache.
    clearCustomerScopedBrowserState();
    return {success: true};
  };
}

export {
  getCustomerAndUserInfo,
  getCustomerInfo,
  getPhotoConfig,
  getUserAccountInfo,
  getGlobalVehicleData,
  getGoogleMapsApiKey,
  getVehicleLabels,
  checkAuthentication,
  getDatabaseUserNames,
  switchCustomer,
  emulateUser,
};
export const {
  updateCustomerInfo,
  updatePhotoConfig,
  updateUserAccountInfo,
  updateVehicles,
  setAuthStatus,
  setAuth,
  setInitialized,
  setIsUserLanguageReloading,
  setIsUserLanguageSwitchAvailable,
  updateAppFilters,
} = appSlice.actions;
export default appSlice.reducer;
